import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { DatePicker } from '@atlaskit/datetime-picker';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Form, { ErrorMessage, Field, MessageWrapper } from '@atlaskit/form';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { JiraIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import type { OptionType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionCallbacks,
	type ActionContext,
	type ActionRendererKeyToPayload,
	type ActionStatusProps,
	type AgentActionPayload,
	type InjectedDependencies,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import { JiraVersionCreateFormFooter } from './jira-version-create-form-footer';
import messages from './messages';
import { ProjectSelector } from './project-selector';

const styles = cssMap({
	inlineSpacing: {
		gap: token('space.200'),
	},
	cardGroup: {
		color: token('color.text'),
		backgroundColor: token('elevation.surface.raised'),
		transition: '200ms',
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
	},
	divider: {
		width: '100%',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	boxSpacing: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	boxSuccess: {
		color: token('color.text'),
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.full'),
		boxShadow: token('elevation.shadow.raised'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingTop: token('space.050'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.100'),
		marginTop: token('space.500'),
		marginBottom: token('space.500'),
	},
});

//JiraVersionCreate component also renders outside of the unified renderer, so we need to keep the old props structure
export type JiraVersionCreatedProps = Pick<ActionCallbacks, 'onConfirm' | 'onCancel'> &
	ActionStatusProps &
	AgentActionPayload<ActionRendererKeyToPayload[typeof ActionKeys.JiraVersionCreate]> &
	ActionContext &
	Pick<InjectedDependencies, 'sendMessageConfirmedTools'>;

export const JiraVersionCreateAction = ({
	onConfirm,
	actions,
	getReadonly,
	getStatus,
	cloudId,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: JiraVersionCreatedProps): React.JSX.Element => {
	const intl = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [hasDateValidationError, setHasDateValidationError] = useState(false);
	const firstAction = actions[0];
	const status = getStatus(firstAction);
	const { data } = firstAction;
	const { name, description, startDate, releaseDate, projectId, driver } = data;

	const [selectedProject, setSelectedProject] = useState<
		(OptionType & { iconUrl?: string }) | undefined
	>(projectId ? { label: data.name, value: projectId } : undefined);

	const handleProjectChange = useCallback(
		({
			projectId,
			projectName,
			iconUrl,
		}: {
			projectId: string;
			projectName: string;
			iconUrl?: string;
		}) => {
			setSelectedProject({ label: projectName, value: projectId, iconUrl });
		},
		[],
	);

	const handleSubmit = useCallback(
		(data: ActionRendererKeyToPayload[typeof ActionKeys.JiraVersionCreate]) => {
			const mergedData = {
				...data,
				driver: driver || null,
				projectId: String(selectedProject?.value ?? projectId ?? ''),
			};

			if (
				FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
			) {
				const idleActions = actions.filter((action) => getStatus(action) === 'idle');
				sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: {
						interactionSource: 'jiraVersionCreateRenderer',
					},
					confirmed_tools: {
						message_id: messageId ?? '',
						tools: idleActions.map((action) =>
							action.invocationId === firstAction.invocationId
								? buildToolFromAction(action, 'confirmed', mergedData)
								: buildToolFromAction(action, 'cancelled'),
						),
					},
				});
			} else {
				onConfirm({
					key: firstAction.key,
					invocationId: firstAction.invocationId,
					data: mergedData,
					actionId: firstAction.actionId,
					status: firstAction.status,
				});
			}

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'submit',
				actionSubject: 'rovoJiraCreateVersionSubmit',
			});
		},
		[
			firstAction,
			actions,
			onConfirm,
			sendAnalyticsEvent,
			selectedProject?.value,
			projectId,
			driver,
			sendMessageConfirmedTools,
			recipientAgentId,
			conversationId,
			messageId,
			getStatus,
		],
	);

	const validateDatePeriod = (
		startDateValue: string | undefined,
		releaseDateValue: string | undefined,
	) => {
		const isValid =
			!Boolean(startDateValue) ||
			!Boolean(releaseDateValue) ||
			new Date(startDateValue!) <= new Date(releaseDateValue!);
		setHasDateValidationError(!isValid);
		return !isValid ? 'INVALID_DATE_PERIOD' : undefined;
	};

	const readonly = getReadonly(firstAction);

	const today = new Date();

	const getTodayDate = () =>
		intl.formatDate(today, {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		});
	const getTwoWeeksFromNow = () => {
		const twoWeeksLater = new Date(today);
		twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);
		return intl.formatDate(twoWeeksLater, {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		});
	};

	return (
		<>
			<Text>{intl.formatMessage(messages.presentationText)}</Text>
			{fg('platform-design_system_team-form_conversion') ? (
				<Form onSubmit={handleSubmit} isDisabled={readonly}>
					<Box xcss={styles.cardGroup}>
						<Box xcss={styles.boxSpacing}>
							<Inline alignBlock="center" space="space.075">
								<JiraIcon appearance="brand" shouldUseNewLogoDesign size="xsmall" />

								<Text>{intl.formatMessage(messages.createVersionHeading)}</Text>
							</Inline>
						</Box>
						<Box xcss={styles.divider}>
							{fg('enable-create-releases-project-dropdown') && (
								<Field
									name="project"
									id="jira-version-create-project-selector-input"
									isRequired
									defaultValue={projectId || ''}
									label={intl.formatMessage(messages.projectSelectorLabel)}
								>
									{() => (
										<ProjectSelector
											siteId={cloudId}
											initialProjectId={projectId}
											onProjectChangeSelect={handleProjectChange}
											selectedProject={selectedProject}
											isDisabled={readonly}
										/>
									)}
								</Field>
							)}
							<Field
								name="name"
								isRequired
								label={intl.formatMessage(messages.nameLabel)}
								defaultValue={name}
								validate={(value, _, fieldState) =>
									(fieldState.dirty || fieldState.touched) && !value ? 'EMPTY_NAME' : undefined
								}
							>
								{({ fieldProps, error }) => (
									<>
										<Textfield {...fieldProps} />
										<MessageWrapper>
											{Boolean(error) && (
												<ErrorMessage>
													{intl.formatMessage(messages.nameValidationError)}
												</ErrorMessage>
											)}
										</MessageWrapper>
									</>
								)}
							</Field>
							<Field<string, HTMLTextAreaElement>
								name="description"
								label={intl.formatMessage(messages.descriptionLabel)}
								defaultValue={description}
							>
								{({ fieldProps }) => <TextArea {...fieldProps} />}
							</Field>
							<Inline xcss={styles.inlineSpacing}>
								<Field
									name="startDate"
									label={intl.formatMessage(messages.startDateLabel)}
									defaultValue={startDate}
									validate={(value, formState) =>
										// @ts-expect-error - formState type from @atlaskit/form does not expose startDate/releaseDate; upstream type gap
										validateDatePeriod(value, formState.releaseDate)
									}
								>
									{({ fieldProps }) => (
										<DatePicker
											locale={intl.locale}
											clearControlLabel={intl.formatMessage(messages.clearChooseDate)}
											shouldShowCalendarButton
											inputLabelId="date"
											openCalendarLabel={intl.formatMessage(messages.openCalendar)}
											placeholder={getTodayDate()}
											{...fieldProps}
										/>
									)}
								</Field>
								<Field
									name="releaseDate"
									label={intl.formatMessage(messages.releaseDateLabel)}
									defaultValue={releaseDate}
									validate={(value, formState) =>
										// @ts-expect-error - formState type from @atlaskit/form does not expose startDate/releaseDate; upstream type gap
										validateDatePeriod(formState.startDate, value)
									}
								>
									{({ fieldProps }) => (
										<DatePicker
											locale={intl.locale}
											clearControlLabel={intl.formatMessage(messages.clearChooseDate)}
											shouldShowCalendarButton
											inputLabelId="date"
											openCalendarLabel={intl.formatMessage(messages.openCalendar)}
											placeholder={getTwoWeeksFromNow()}
											{...fieldProps}
										/>
									)}
								</Field>
							</Inline>
							<MessageWrapper>
								{hasDateValidationError && (
									<ErrorMessage>{intl.formatMessage(messages.dateValidationError)}</ErrorMessage>
								)}
							</MessageWrapper>
						</Box>
						<Box xcss={styles.divider}>
							<JiraVersionCreateFormFooter readonly={readonly} status={status} />
						</Box>
					</Box>
				</Form>
			) : (
				<Form onSubmit={handleSubmit} isDisabled={readonly}>
					{({ formProps }) => (
						<form {...formProps}>
							<Box xcss={styles.cardGroup}>
								<Box xcss={styles.boxSpacing}>
									<Inline alignBlock="center" space="space.075">
										<JiraIcon appearance="brand" shouldUseNewLogoDesign size="xsmall" />

										<Text>{intl.formatMessage(messages.createVersionHeading)}</Text>
									</Inline>
								</Box>
								<Box xcss={styles.divider}>
									{fg('enable-create-releases-project-dropdown') && (
										<Field
											name="project"
											id="jira-version-create-project-selector-input"
											isRequired
											defaultValue={projectId}
											label={intl.formatMessage(messages.projectSelectorLabel)}
										>
											{() => (
												<ProjectSelector
													siteId={cloudId}
													initialProjectId={projectId}
													onProjectChangeSelect={handleProjectChange}
													selectedProject={selectedProject}
													isDisabled={readonly}
												/>
											)}
										</Field>
									)}
									<Field
										name="name"
										isRequired
										label={intl.formatMessage(messages.nameLabel)}
										defaultValue={name}
										validate={(value, _, fieldState) =>
											(fieldState.dirty || fieldState.touched) && !value ? 'EMPTY_NAME' : undefined
										}
									>
										{({ fieldProps, error }) => (
											<>
												<Textfield {...fieldProps} />
												<MessageWrapper>
													{Boolean(error) && (
														<ErrorMessage>
															{intl.formatMessage(messages.nameValidationError)}
														</ErrorMessage>
													)}
												</MessageWrapper>
											</>
										)}
									</Field>
									<Field<string, HTMLTextAreaElement>
										name="description"
										label={intl.formatMessage(messages.descriptionLabel)}
										defaultValue={description}
									>
										{({ fieldProps }) => <TextArea {...fieldProps} />}
									</Field>
									<Inline xcss={styles.inlineSpacing}>
										<Field
											name="startDate"
											label={intl.formatMessage(messages.startDateLabel)}
											defaultValue={startDate}
											validate={(value, formState) =>
												// @ts-expect-error - formState type from @atlaskit/form does not expose startDate/releaseDate; upstream type gap
												validateDatePeriod(value, formState.releaseDate)
											}
										>
											{({ fieldProps }) => (
												<DatePicker
													locale={intl.locale}
													clearControlLabel={intl.formatMessage(messages.clearChooseDate)}
													shouldShowCalendarButton
													inputLabelId="date"
													openCalendarLabel={intl.formatMessage(messages.openCalendar)}
													placeholder={getTodayDate()}
													{...fieldProps}
												/>
											)}
										</Field>
										<Field
											name="releaseDate"
											label={intl.formatMessage(messages.releaseDateLabel)}
											defaultValue={releaseDate}
											validate={(value, formState) =>
												// @ts-expect-error - formState type from @atlaskit/form does not expose startDate/releaseDate; upstream type gap
												validateDatePeriod(formState.startDate, value)
											}
										>
											{({ fieldProps }) => (
												<DatePicker
													locale={intl.locale}
													clearControlLabel={intl.formatMessage(messages.clearChooseDate)}
													shouldShowCalendarButton
													inputLabelId="date"
													openCalendarLabel={intl.formatMessage(messages.openCalendar)}
													placeholder={getTwoWeeksFromNow()}
													{...fieldProps}
												/>
											)}
										</Field>
									</Inline>
									<MessageWrapper>
										{hasDateValidationError && (
											<ErrorMessage>
												{intl.formatMessage(messages.dateValidationError)}
											</ErrorMessage>
										)}
									</MessageWrapper>
								</Box>
								<Box xcss={styles.divider}>
									<JiraVersionCreateFormFooter readonly={readonly} status={status} />
								</Box>
							</Box>
						</form>
					)}
				</Form>
			)}
			{status === 'started' && (
				<Inline alignInline="center">
					<Box xcss={styles.boxSuccess}>
						<Inline space="space.075" alignBlock="center">
							<CheckMarkIcon
								size="small"
								label={intl.formatMessage(messages.createReleaseSelected)}
								color={token('color.icon.success')}
							/>
							<Text size="small">{intl.formatMessage(messages.createReleaseSelected)}</Text>
						</Inline>
					</Box>
				</Inline>
			)}
		</>
	);
};
